import React, { useEffect, useState, useCallback, useRef } from 'react';
import { Search, X, Loader2, ChevronRight, Store } from 'lucide-react';
import { logFoodAPI } from '../../../utils/api';
import RecentlyLoggedList from '../../../components/logfood/RecentlyLoggedList';
import FallbackChips from '../../../components/logfood/FallbackChips';
import FatSecretAttribution from '../../../components/logfood/FatSecretAttribution';

const PAGE_SIZE = 10;
const ENABLED = process.env.REACT_APP_ENABLE_FATSECRET === 'true';

/**
 * Restaurants tab — step 1 of a 2-step flow.
 *
 * Empty / landing → recently logged restaurant items.
 * With query → list of restaurant CHAINS whose name or menu matches.
 * Tapping a chain row routes to <RestaurantMenuView> (handled by parent).
 */
export default function RestaurantsTab({
  recents,
  recentsLoading,
  onSelectFood,
  onSelectRecent,
  onQuickLog,
  onQuickLogRecent,
  onOpenChain,
  onTryTab,
  onCreateManual,
  onSearchAllRecents,
}) {
  const [query, setQuery] = useState('');
  const [chains, setChains] = useState([]);
  const [matchingRecents, setMatchingRecents] = useState([]);
  const [page, setPage] = useState(0);
  const [total, setTotal] = useState(0);
  const [fallback, setFallback] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const debounceRef = useRef(null);
  // Latest-request guard: only the most recent search (query + page) may
  // update state, so an older in-flight response can't overwrite a newer one.
  const requestSeqRef = useRef(0);

  const doSearch = useCallback(function (q, pg) {
    const seq = ++requestSeqRef.current;
    if (!q || !q.trim()) {
      setChains([]);
      setMatchingRecents([]);
      setTotal(0);
      setFallback(null);
      setError(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    Promise.all([
      logFoodAPI.searchRestaurants({ q: q, page: pg, limit: PAGE_SIZE }).catch(function (err) {
        return { error: err };
      }),
      logFoodAPI.getRecentFoods({ scope: 'restaurants', q: q, limit: 3 }).catch(function () {
        return { data: [] };
      }),
    ]).then(function (arr) {
      if (seq !== requestSeqRef.current) return;
      const searchRes = arr[0];
      const recentsRes = arr[1];
      if (searchRes && searchRes.error) {
        const status = searchRes.error.response && searchRes.error.response.status;
        const detail = (searchRes.error.response && searchRes.error.response.data && searchRes.error.response.data.detail) || '';
        console.error('[RestaurantsTab] search/restaurants failed:', { status, detail, message: searchRes.error.message });
        if (status === 503) setError('disabled');
        else if (status === 401) setError('auth');
        else if (status) setError('http_' + status);
        else setError('network');
        setChains([]);
        setTotal(0);
        setFallback(null);
      } else {
        const data = (searchRes && searchRes.data) || {};
        const items = Array.isArray(data.items) ? data.items : [];
        if (pg === 0) {
          setChains(items);
        } else {
          setChains(function (prev) { return prev.concat(items); });
        }
        setTotal(Number(data.total || 0));
        setFallback(data.fallback_suggestion || null);
      }
      setMatchingRecents(Array.isArray(recentsRes.data) ? recentsRes.data.slice(0, 3) : []);
      setLoading(false);
    });
  }, []);

  useEffect(function () {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    setPage(0);
    debounceRef.current = setTimeout(function () { doSearch(query, 0); }, 300);
    return function () { if (debounceRef.current) clearTimeout(debounceRef.current); };
  }, [query, doSearch]);

  const handleShowMore = function () {
    const next = page + 1;
    setPage(next);
    doSearch(query, next);
  };

  const isLanding = !query.trim();

  return (
    <div className="flex flex-col" data-testid="restaurants-tab">
      <div className="px-4 pt-3 pb-2">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-muted-foreground" />
          <input
            type="text"
            value={query}
            onChange={function (e) { setQuery(e.target.value); }}
            placeholder="Search restaurants..."
            className="w-full pl-9 pr-9 py-2.5 rounded-full border border-input bg-background text-sm focus:outline-none focus:ring-2 focus:ring-ring"
            data-testid="restaurants-search-input"
          />
          {query && (
            <button
              type="button"
              onClick={function () { setQuery(''); }}
              className="absolute right-3 top-1/2 -translate-y-1/2 size-5 rounded-full bg-muted text-muted-foreground hover:bg-muted/80 flex items-center justify-center"
              aria-label="Clear search"
              data-testid="restaurants-search-clear"
            >
              <X className="size-3" />
            </button>
          )}
        </div>
      </div>

      {!ENABLED && (
        <div className="px-4 py-12 text-center text-sm text-muted-foreground">
          Restaurant search activates when FatSecret keys are connected.
        </div>
      )}

      {ENABLED && isLanding && (
        <RecentlyLoggedList
          items={recents}
          onSelect={onSelectRecent}
          onQuickLog={onQuickLogRecent}
          emptyMessage={recentsLoading ? 'Loading…' : 'No restaurants logged yet.'}
        />
      )}

      {ENABLED && !isLanding && (
        <>
          {matchingRecents.length > 0 && !loading && (
            <RecentlyLoggedList
              items={matchingRecents}
              onSelect={onSelectRecent}
              onQuickLog={onQuickLogRecent}
              trailingAction={
                <button
                  type="button"
                  className="text-xs text-primary hover:underline"
                  onClick={function () { setQuery(''); }}
                  data-testid="restaurants-search-all-recents"
                >
                  Show all recents →
                </button>
              }
            />
          )}

          {loading && chains.length === 0 && (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="size-6 animate-spin text-muted-foreground" />
            </div>
          )}

          {!loading && error === 'disabled' && (
            <div className="px-4 py-12 text-center text-sm text-muted-foreground">
              Restaurant search activates when FatSecret keys are connected.
            </div>
          )}

          {!loading && error === 'auth' && (
            <div className="px-4 py-12 text-center text-sm text-destructive" data-testid="restaurants-search-error-auth">
              Session expired — please sign in again.
            </div>
          )}

          {!loading && error === 'network' && (
            <div className="px-4 py-12 text-center text-sm text-destructive" data-testid="restaurants-search-error-network">
              Network unreachable. Check your connection and try again.
            </div>
          )}

          {!loading && typeof error === 'string' && error.indexOf('http_') === 0 && (
            <div className="px-4 py-12 text-center text-sm text-destructive" data-testid="restaurants-search-error-http">
              Search failed ({error.replace('http_', 'HTTP ')}). Please try again.
            </div>
          )}

          {!loading && error === 'failed' && (
            <div className="px-4 py-12 text-center text-sm text-destructive">
              Search failed. Please try again.
            </div>
          )}

          {!loading && !error && chains.length > 0 && (
            <>
              <div className="px-4 py-2">
                <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">
                  Restaurants matching "{query}"
                </h3>
              </div>
              <ul className="divide-y divide-border">
                {chains.map(function (chain) {
                  return (
                    <li
                      key={chain.chain_id}
                      className="flex items-center gap-3 px-4 py-3 hover:bg-accent/30 transition-colors"
                      data-testid="restaurants-chain-row"
                    >
                      <button
                        type="button"
                        className="flex-1 flex items-center gap-3 text-left min-w-0"
                        onClick={function () { onOpenChain && onOpenChain(chain); }}
                        data-testid="restaurants-chain-tap"
                      >
                        <span className="size-10 shrink-0 rounded-lg bg-accent/10 text-accent flex items-center justify-center" aria-hidden="true"><Store size={20} /></span>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-foreground truncate">{chain.name}</p>
                          {typeof chain.match_count === 'number' && chain.match_count > 0 && (
                            <p className="text-xs text-muted-foreground">
                              {chain.match_count} menu items match
                            </p>
                          )}
                        </div>
                        <ChevronRight className="size-4 text-muted-foreground shrink-0" />
                      </button>
                    </li>
                  );
                })}
              </ul>
              {chains.length < total && (
                <div className="px-4 py-3 flex justify-center">
                  <button
                    type="button"
                    className="text-sm text-primary hover:underline"
                    onClick={handleShowMore}
                    disabled={loading}
                    data-testid="restaurants-show-more"
                  >
                    {loading ? 'Loading…' : 'Show 10 more'}
                  </button>
                </div>
              )}
            </>
          )}

          {!loading && !error && chains.length === 0 && fallback && (
            <FallbackChips
              suggestion={fallback}
              query={query}
              onTryTab={onTryTab}
              onCreateManual={onCreateManual}
            />
          )}
        </>
      )}

      {ENABLED && <FatSecretAttribution />}
    </div>
  );
}
